import React from 'react';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'brand' | 'white' | 'gray' | 'blue' | 'danger';
  className?: string;
}

/**
 * LoadingSpinner - Single source of truth for all loading spinners
 *
 * This component is the ONLY place where spinner design is defined.
 * To change the spinner design globally, edit this file.
 *
 * Features:
 * - SVG-based smooth animation
 * - Multiple sizes (xs to xl)
 * - Multiple colors (primary, brand, white, gray, blue, danger)
 * - Supports brand color via CSS variables for booking pages
 *
 * Usage:
 * <LoadingSpinner size="md" color="primary" />
 * <LoadingSpinner size="sm" color="white" />
 * <LoadingSpinner size="lg" color="brand" /> // Uses meeting brand color
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = ''
}) => {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };

  // Use CSS variable for brand color to respect meeting-specific branding
  const getColorStyle = () => {
    if (color === 'brand') {
      return { color: 'var(--brand-color, #2E7AA0)' };
    }
    return undefined;
  };

  const colorClasses = {
    primary: 'text-primary-600',
    brand: '', // Color handled by inline style
    white: 'text-white',
    gray: 'text-gray-400',
    blue: 'text-blue-600',
    danger: 'text-red-600'
  };

  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <svg
        className={`animate-spin ${sizeClasses[size]} ${colorClasses[color]}`}
        style={getColorStyle()}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
};

export default LoadingSpinner;